import { hasDisplayExponent } from "../design-system/format/money.ts";
import {
  assertValidRequirements,
  RECIPIENT_REQUIREMENT_SETS,
  type RecipientRequirements,
} from "./receiver/recipient-requirements.ts";

/**
 * Tenant configuration — the white-label boundary.
 *
 * `CLAUDE.md` § Product boundary: "Nura is a bank-branded transfer
 * experience/orchestration SDK." The bank is the institution a person sees;
 * Nura is the mechanism underneath. A design system for this product is
 * therefore wrong if it has one look — it has to carry a different bank's
 * identity on every surface without any of them looking like a template, and
 * without a component ever being forked to do it.
 *
 * ## Two tiers, and why the split is where it is
 *
 * A tenant supplies **brand** and **product configuration**. It cannot supply
 * anything else. Type, spacing, hierarchy, motion, every semantic state colour
 * (success, attention, danger, info) and the focus treatment stay Nura's,
 * because those carry meaning rather than identity: a bank that could restyle
 * "danger" could make a declined transfer look approved, and a bank that could
 * restyle the focus ring could make its interface unusable by keyboard.
 * `frontend/test/tenant.test.ts` asserts that applying a tenant writes
 * `--nura-brand-*` properties and nothing else.
 *
 * The Nura reference identity is itself a tenant in this list rather than a
 * hard-coded default, so the default styling path and the bank styling path are
 * the same path — the one that gets exercised on every screen.
 *
 * **Every institution below other than Nura is fictional** and exists only to
 * demonstrate the theming surface. No real bank's name, mark or palette appears
 * here.
 */

/**
 * How visible Nura is on a partner's surface.
 *
 * Nura is white-label, and it is also a brand worth building. Those pull in
 * opposite directions, so the balance is a commercial decision per deployment
 * rather than a design one baked into components:
 *
 * - `nura` — Nura is the visible identity. The reference build.
 * - `powered-by-nura` — the bank is primary; a quiet attribution sits in the
 *   footer. Nura's mark keeps its own colour rather than imitating the bank,
 *   because an attribution painted in the host's brand attributes nothing.
 * - `none` — the bank only. No customer-facing Nura mark or wording.
 *
 * `none` hides Nura's **branding**, not Nura's **design system**. The aperture
 * continues to carry step and ready states, where it is an interaction motif
 * rather than a mark — a signature that cannot be turned off would be covert
 * branding, and a partner who bought a white-label product did not buy that.
 */
export type AttributionMode = "nura" | "powered-by-nura" | "none";

/** The six colour values a tenant controls. Nothing else is overridable. */
export interface TenantBrand {
  /** Primary brand. Drives the primary CTA, selected controls, active progress. */
  readonly brand: string;
  /** Hover and pressed state of the primary brand. */
  readonly brandStrong: string;
  /** Foreground on `brand` and `brandStrong`. Must meet AA on both. */
  readonly brandOn: string;
  /** Quiet brand-tinted surface for supporting and selected states. */
  readonly brandSubtle: string;
  /** Foreground on `brandSubtle`. Must meet AA. */
  readonly brandOnSubtle: string;
  /** Small brand detail — wordmark mark, brand iconography. Held to the AA non-text minimum. */
  readonly brandAccent: string;
}

/**
 * Currencies a tenant transacts in.
 *
 * The selector renders `supportedCurrencies` **in the order given**, so a bank
 * controls precedence as well as membership. A single-currency institution
 * supplies one code and the selector disappears rather than showing one
 * pointless option.
 *
 * Codes only. Minor units come from the pinned ISO 4217 snapshot — see
 * `format/money.ts`. A tenant cannot declare a currency's scale, because scale
 * is not a branding decision.
 */
export interface TenantCurrencyConfig {
  readonly defaultCurrency: string;
  readonly supportedCurrencies: readonly string[];
}

export interface Tenant {
  readonly id: string;
  /** Wordmark text shown in the app bar. */
  readonly name: string;
  /** Two-letter mark shown in the app bar chip. */
  readonly initials: string;
  /** True for the Nura reference identity, which is not a bank. */
  readonly isReference?: boolean;
  /** How visible Nura is on this deployment. */
  readonly attribution: AttributionMode;
  readonly currency: TenantCurrencyConfig;
  /**
   * What this institution needs in order to deliver money to a recipient.
   *
   * A third piece of product configuration, alongside currency — and, like
   * currency, chosen from a vocabulary Nura owns rather than supplied freely.
   * The sending bank decides *which* details it requires; it does not get to
   * decide what a field means, how it is labelled, what keyboard it opens, or
   * whether it is a credential. See
   * `receiver/recipient-requirements.ts`.
   */
  readonly recipient: RecipientRequirements;
  readonly light: TenantBrand;
  readonly dark: TenantBrand;
}

export const TENANTS: readonly Tenant[] = [
  {
    // The Nura reference identity: deep ink structure, mineral teal brand.
    id: "nura",
    name: "Nura",
    initials: "NU",
    isReference: true,
    attribution: "nura",
    currency: {
      defaultCurrency: "EUR",
      supportedCurrencies: ["EUR", "USD", "AED", "GBP"],
    },
    recipient: RECIPIENT_REQUIREMENT_SETS.nameAndIban,
    light: {
      brand: "#0f766e",
      brandStrong: "#0c6560",
      brandOn: "#ffffff",
      brandSubtle: "#e6f2f0",
      brandOnSubtle: "#0a5450",
      brandAccent: "#0d9488",
    },
    dark: {
      brand: "#34d3c0",
      brandStrong: "#5fe3d2",
      brandOn: "#04211f",
      brandSubtle: "#10322f",
      brandOnSubtle: "#7fe8d8",
      brandAccent: "#2dd4bf",
    },
  },
  {
    // Fictional. A Gulf institution with a two-currency configuration.
    id: "qasr",
    name: "Qasr Bank",
    initials: "QB",
    // Co-branded: Qasr is primary, with a quiet "Powered by Nura" in the footer.
    attribution: "powered-by-nura",
    currency: {
      defaultCurrency: "AED",
      supportedCurrencies: ["AED", "USD"],
    },
    // A different set from the reference tenant's, which is the point: the
    // renderer never learns which bank it is drawing for.
    recipient: RECIPIENT_REQUIREMENT_SETS.namePhoneAndAccount,
    light: {
      brand: "#7c2d5a",
      brandStrong: "#5f2145",
      brandOn: "#ffffff",
      brandSubtle: "#f7ecf2",
      brandOnSubtle: "#66204a",
      brandAccent: "#a8763a",
    },
    dark: {
      brand: "#f0aad0",
      brandStrong: "#f7c9e2",
      brandOn: "#26071a",
      brandSubtle: "#331025",
      brandOnSubtle: "#f6c2dd",
      brandAccent: "#e0b070",
    },
  },
  {
    // Fictional. A Nordic institution, single currency.
    id: "lumen",
    name: "Lumen Sparbank",
    initials: "LS",
    // Fully white-labelled: no customer-facing Nura mark or wording.
    attribution: "none",
    currency: {
      defaultCurrency: "SEK",
      supportedCurrencies: ["SEK"],
    },
    recipient: RECIPIENT_REQUIREMENT_SETS.nameAndIban,
    light: {
      brand: "#14532d",
      brandStrong: "#0b3a1e",
      brandOn: "#ffffff",
      brandSubtle: "#e8f3ec",
      brandOnSubtle: "#134e2a",
      brandAccent: "#a35a12",
    },
    dark: {
      brand: "#86efac",
      brandStrong: "#bbf7d0",
      brandOn: "#062012",
      brandSubtle: "#12291c",
      brandOnSubtle: "#a7f3c4",
      brandAccent: "#f0b350",
    },
  },
];

/** Raised when a tenant's currency configuration cannot be honoured. */
export class TenantConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TenantConfigError";
  }
}

/**
 * Validate a currency configuration.
 *
 * Fails loudly rather than degrading: a tenant configured with a currency Nura
 * holds no pinned minor-unit count for cannot have its amounts rendered
 * exactly, and rendering them inexactly is the outcome `CLAUDE.md`
 * § Transaction integrity exists to prevent.
 */
export function assertValidTenantConfig(tenant: Tenant): void {
  assertValidCurrencyConfig(tenant);
  assertValidRequirements(tenant.recipient);
}

export function assertValidCurrencyConfig(tenant: Tenant): void {
  const { defaultCurrency, supportedCurrencies } = tenant.currency;

  if (supportedCurrencies.length === 0) {
    throw new TenantConfigError(`${tenant.id}: supportedCurrencies must not be empty`);
  }
  if (!supportedCurrencies.includes(defaultCurrency)) {
    throw new TenantConfigError(
      `${tenant.id}: defaultCurrency ${defaultCurrency} is not in supportedCurrencies`,
    );
  }
  if (new Set(supportedCurrencies).size !== supportedCurrencies.length) {
    throw new TenantConfigError(`${tenant.id}: supportedCurrencies contains duplicates`);
  }
  for (const code of supportedCurrencies) {
    if (!hasDisplayExponent(code)) {
      throw new TenantConfigError(`${tenant.id}: no pinned ISO 4217 minor-unit count for ${code}`);
    }
  }
}

export function tenantById(id: string): Tenant {
  return TENANTS.find((tenant) => tenant.id === id) ?? (TENANTS[0] as Tenant);
}

/** The `--nura-brand-*` custom properties, and the only ones a tenant may set. */
export const TENANT_BRAND_PROPERTIES = [
  "--nura-brand",
  "--nura-brand-strong",
  "--nura-brand-on",
  "--nura-brand-subtle",
  "--nura-brand-on-subtle",
  "--nura-brand-accent",
] as const;

/** Apply a tenant's brand tokens for the active theme to the document root. */
export function applyTenant(tenant: Tenant, theme: "light" | "dark"): void {
  const brand = theme === "dark" ? tenant.dark : tenant.light;
  const values: readonly string[] = [
    brand.brand,
    brand.brandStrong,
    brand.brandOn,
    brand.brandSubtle,
    brand.brandOnSubtle,
    brand.brandAccent,
  ];
  const root = document.documentElement;
  for (const [index, property] of TENANT_BRAND_PROPERTIES.entries()) {
    const value = values[index];
    if (value !== undefined) root.style.setProperty(property, value);
  }
}
